import { useState, useEffect, useCallback } from 'react';
import { Card } from '@atoms/index';
import { PlatformLegalDocumentsSection } from '@organisms/PlatformLegalDocumentsSection';
import { adminService } from '@services/adminService';
import { cn } from '@/utils/cn';

interface KnownNumericSetting {
  key: string;
  label: string;
  description: string;
  unit?: string;
  min?: number;
  max?: number;
  defaultValue: string;
  inputHint?: string;
  apiCategory: string;
  apiDataType: 'number';
}

interface KnownTextSetting {
  key: string;
  label: string;
  description: string;
  defaultValue: string;
  maxLength: number;
  rows?: number;
  apiCategory: string;
  apiDataType: 'string';
}

const DISPATCH_SETTINGS: KnownNumericSetting[] = [
  {
    key: 'UNASSIGNED_ORDER_CANCEL_MINUTES',
    label: 'Otomatik Sipariş İptali',
    description:
      'Bir sipariş oluşturulup restoran tarafından hazırlanmaya başlandıktan sonra hiçbir kuryeye atanamazsa, bu süre dolduğunda sipariş otomatik olarak iptal edilir ve müşteriye iade yapılır.',
    unit: 'dakika',
    min: 10,
    max: 1440,
    defaultValue: '60',
    inputHint: 'Önerilen: 30–120 dakika',
    apiCategory: 'DISPATCH',
    apiDataType: 'number',
  },
];

const ORDER_LIFECYCLE_NUMERIC_SETTINGS: KnownNumericSetting[] = [
  {
    key: 'ORDER_PREPARATION_DELAY_NOTIFY_MINUTES',
    label: 'Gecikme bilgilendirmesi',
    description:
      'Restoran henüz hazırlamaya başlamadıysa (sipariş CREATED veya PENDING), bu süre dolduğunda müşteriye “gecikme” bildirimi gönderilir. Otomatik iptal değildir.',
    unit: 'dakika',
    min: 1,
    max: 180,
    defaultValue: '35',
    inputHint: 'Genelde otomatik iptal süresinden kısa tutun (ör. iptal 40 dk ise uyarı 30–35 dk).',
    apiCategory: 'ORDER_LIFECYCLE',
    apiDataType: 'number',
  },
  {
    key: 'ORDER_RESTAURANT_INACTION_TIMEOUT_MINUTES',
    label: 'Restoran yanıt süresi (otomatik iptal)',
    description:
      'Sipariş oluşturulduktan veya (ileri tarihli siparişte) restorana düştükten sonra restoran bu süre içinde hazırlamaya başlamazsa sipariş otomatik iptal edilir. Kartlı ödemelerde iade tetiklenir; kapıda ödemede yalnızca bilgilendirme.',
    unit: 'dakika',
    min: 5,
    max: 180,
    defaultValue: '40',
    inputHint: 'Önerilen: 35–60 dakika',
    apiCategory: 'ORDER_LIFECYCLE',
    apiDataType: 'number',
  },
];

const ORDER_LIFECYCLE_TEXT_SETTINGS: KnownTextSetting[] = [
  {
    key: 'ORDER_RESTAURANT_INACTION_CANCEL_REASON_TR',
    label: 'Otomatik iptal gerekçesi (müşteri)',
    description:
      'Süre aşımıyla iptal edildiğinde sipariş kaydına ve müşteri bildirimlerine yazılacak Türkçe metin (en fazla 1000 karakter).',
    defaultValue:
      'Restoran belirlenen süre içinde siparişi kabul etmedi veya hazırlamaya başlamadı. Siparişiniz iptal edildi.',
    maxLength: 1000,
    rows: 4,
    apiCategory: 'ORDER_LIFECYCLE',
    apiDataType: 'string',
  },
];

interface SettingCardProps {
  setting: KnownNumericSetting;
  currentValue: string;
  onSave: (
    key: string,
    value: string,
    meta?: { category?: string; description?: string; dataType?: string },
  ) => Promise<void>;
}

const SettingCard = ({ setting, currentValue, onSave }: SettingCardProps) => {
  const [editValue, setEditValue] = useState(currentValue);
  const [isSaving, setIsSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const numericValue = parseFloat(editValue);
  const isDirty = editValue !== currentValue;

  const validate = (raw: string): string | null => {
    const n = parseFloat(raw);
    if (!Number.isFinite(n) || !Number.isInteger(n)) return 'Lütfen tam sayı giriniz.';
    if (setting.min !== undefined && n < setting.min)
      return `En az ${setting.min} ${setting.unit ?? ''} olmalıdır.`;
    if (setting.max !== undefined && n > setting.max)
      return `En fazla ${setting.max} ${setting.unit ?? ''} olabilir.`;
    return null;
  };

  const handleSave = async () => {
    if (!isDirty || isSaving) return;
    const validationError = validate(editValue.trim());
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setIsSaving(true);
    try {
      await onSave(setting.key, editValue.trim(), {
        category: setting.apiCategory,
        dataType: setting.apiDataType,
        description: setting.description.slice(0, 500),
      });
      setSavedAt(new Date());
    } catch {
      setError('Kaydedilemedi. Lütfen tekrar deneyin.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (val: string) => {
    setEditValue(val);
    setError(null);
    setSavedAt(null);
  };

  const progressPercent =
    setting.min !== undefined && setting.max !== undefined
      ? Math.min(
          100,
          Math.max(
            0,
            ((numericValue - setting.min) / (setting.max - setting.min)) * 100
          )
        )
      : null;

  const getProgressColor = () => {
    if (progressPercent === null) return 'bg-[#E63946]';
    if (progressPercent < 30) return 'bg-green-500';
    if (progressPercent < 70) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const getValueLabel = () => {
    if (!Number.isFinite(numericValue)) return editValue;
    if (numericValue >= 60) {
      const h = Math.floor(numericValue / 60);
      const m = numericValue % 60;
      if (m === 0) return `${h} saat`;
      return `${h} sa ${m} dk`;
    }
    return `${numericValue} dakika`;
  };

  return (
    <Card>
      <div className="space-y-4">
        {/* Header */}
        <div>
          <h3 className="text-base font-bold text-gray-900">{setting.label}</h3>
          <p className="text-sm text-gray-500 mt-1 leading-relaxed">{setting.description}</p>
        </div>

        {/* Current value display */}
        <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
          <div className="flex-1">
            <p className="text-xs text-gray-400 mb-0.5">Mevcut değer</p>
            <p className="text-lg font-bold text-gray-900">{getValueLabel()}</p>
          </div>
          {isDirty && (
            <div className="text-right">
              <p className="text-xs text-gray-400 mb-0.5">Yeni değer</p>
              <p className="text-lg font-bold text-[#E63946]">
                {Number.isFinite(parseFloat(editValue)) ? getValueLabel() : '—'}
              </p>
            </div>
          )}
        </div>

        {/* Slider + input */}
        {setting.min !== undefined && setting.max !== undefined && (
          <div>
            <div className="flex items-center justify-between mb-2 text-xs text-gray-400">
              <span>{setting.min} dk</span>
              <span>{setting.max >= 60 ? `${setting.max / 60} sa` : `${setting.max} dk`}</span>
            </div>
            <input
              type="range"
              min={setting.min}
              max={setting.max}
              step={5}
              value={Number.isFinite(numericValue) ? numericValue : setting.min}
              onChange={(e) => handleChange(e.target.value)}
              className="w-full h-2 rounded-full appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, ${
                  progressPercent !== null && progressPercent < 30
                    ? '#22c55e'
                    : progressPercent !== null && progressPercent < 70
                    ? '#f59e0b'
                    : '#E63946'
                } 0%, ${
                  progressPercent !== null && progressPercent < 30
                    ? '#22c55e'
                    : progressPercent !== null && progressPercent < 70
                    ? '#f59e0b'
                    : '#E63946'
                } ${progressPercent ?? 50}%, #e5e7eb ${progressPercent ?? 50}%, #e5e7eb 100%)`,
              }}
            />
          </div>
        )}

        {/* Manual input row */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <input
              type="number"
              min={setting.min}
              max={setting.max}
              value={editValue}
              onChange={(e) => handleChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape') {
                  setEditValue(currentValue);
                  setError(null);
                }
              }}
              className={cn(
                'w-full px-4 py-2.5 pr-16 text-sm rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-[#E63946]/30',
                error
                  ? 'border-red-300 bg-red-50'
                  : isDirty
                  ? 'border-[#E63946]/40 bg-white'
                  : 'border-gray-200 bg-gray-50'
              )}
            />
            {setting.unit && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
                {setting.unit}
              </span>
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={!isDirty || isSaving}
            className={cn(
              'px-5 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap',
              isDirty && !isSaving
                ? 'bg-[#E63946] text-white hover:bg-[#c62e3a] cursor-pointer shadow-sm'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            )}
          >
            {isSaving ? (
              <span className="flex items-center gap-1.5">
                <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Kaydediliyor
              </span>
            ) : (
              'Kaydet'
            )}
          </button>
        </div>

        {setting.inputHint && !error && !savedAt && (
          <p className="text-xs text-gray-400">{setting.inputHint}</p>
        )}
        {error && <p className="text-xs text-red-500 flex items-center gap-1">⚠️ {error}</p>}
        {savedAt && !error && (
          <p className="text-xs text-green-600 flex items-center gap-1">
            ✓ Kaydedildi — {savedAt.toLocaleTimeString('tr-TR')}
          </p>
        )}
      </div>
    </Card>
  );
};

interface TextSettingCardProps {
  setting: KnownTextSetting;
  currentValue: string;
  onSave: (
    key: string,
    value: string,
    meta?: { category?: string; description?: string; dataType?: string },
  ) => Promise<void>;
}

const TextSettingCard = ({ setting, currentValue, onSave }: TextSettingCardProps) => {
  const [editValue, setEditValue] = useState(currentValue);
  const [isSaving, setIsSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEditValue(currentValue);
  }, [currentValue]);

  const isDirty = editValue !== currentValue;

  const handleSave = async () => {
    if (!isDirty || isSaving) return;
    const trimmed = editValue.trim();
    if (trimmed.length > setting.maxLength) {
      setError(`En fazla ${setting.maxLength} karakter.`);
      return;
    }
    setError(null);
    setIsSaving(true);
    try {
      await onSave(setting.key, trimmed, {
        category: setting.apiCategory,
        dataType: setting.apiDataType,
        description: setting.description.slice(0, 500),
      });
      setSavedAt(new Date());
    } catch {
      setError('Kaydedilemedi. Lütfen tekrar deneyin.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <div className="space-y-4">
        <div>
          <h3 className="text-base font-bold text-gray-900">{setting.label}</h3>
          <p className="text-sm text-gray-500 mt-1 leading-relaxed">{setting.description}</p>
        </div>
        <textarea
          value={editValue}
          onChange={(e) => {
            setEditValue(e.target.value);
            setError(null);
            setSavedAt(null);
          }}
          rows={setting.rows ?? 4}
          maxLength={setting.maxLength}
          className={cn(
            'w-full px-4 py-3 text-sm rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-[#E63946]/30 resize-y min-h-[100px]',
            error
              ? 'border-red-300 bg-red-50'
              : isDirty
              ? 'border-[#E63946]/40 bg-white'
              : 'border-gray-200 bg-gray-50',
          )}
        />
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-400">
            {editValue.length} / {setting.maxLength}
          </p>
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || isSaving}
            className={cn(
              'px-5 py-2.5 rounded-xl text-sm font-semibold transition-all',
              isDirty && !isSaving
                ? 'bg-[#E63946] text-white hover:bg-[#c62e3a] cursor-pointer shadow-sm'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed',
            )}
          >
            {isSaving ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </div>
        {error && <p className="text-xs text-red-500">⚠️ {error}</p>}
        {savedAt && !error && (
          <p className="text-xs text-green-600">✓ Kaydedildi — {savedAt.toLocaleTimeString('tr-TR')}</p>
        )}
      </div>
    </Card>
  );
};

const PlatformSettingsTab = () => {
  const [values, setValues] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await adminService.getSettings();
      const map: Record<string, string> = {};
      data.forEach((s) => { map[s.key] = s.value; });
      setValues(map);
    } catch {
      setLoadError('Ayarlar yüklenemedi. Lütfen sayfayı yenileyin.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async (
    key: string,
    value: string,
    meta?: { category?: string; description?: string; dataType?: string },
  ) => {
    await adminService.updateSetting(key, value, meta);
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Platform Ayarları</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Platformun otomatik işlemlerini buradan yönetebilirsiniz.
        </p>
      </div>

      {isLoading && (
        <div className="flex items-center gap-3 py-16 justify-center text-gray-500">
          <div className="w-5 h-5 border-2 border-gray-200 border-t-[#E63946] rounded-full animate-spin" />
          <span className="text-sm">Yükleniyor...</span>
        </div>
      )}

      {loadError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
          <span>⚠️</span> {loadError}
        </div>
      )}

      {!isLoading && !loadError && (
        <div className="max-w-2xl space-y-8">
          {/* Dispatch section */}
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-xl bg-orange-100 flex items-center justify-center">
                <span className="text-base">🚴</span>
              </div>
              <div>
                <h2 className="text-sm font-bold text-gray-800">Sipariş & Kurye Yönetimi</h2>
                <p className="text-xs text-gray-400">Otomatik atama ve iptal kuralları</p>
              </div>
            </div>
            <div className="space-y-4">
              {DISPATCH_SETTINGS.map((s) => (
                <SettingCard
                  key={s.key}
                  setting={s}
                  currentValue={values[s.key] ?? s.defaultValue}
                  onSave={handleSave}
                />
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2.5 mb-4 mt-10">
              <div className="w-8 h-8 rounded-xl bg-[#E63946]/10 flex items-center justify-center">
                <span className="text-base">⏱️</span>
              </div>
              <div>
                <h2 className="text-sm font-bold text-gray-800">Restoran yanıt & sipariş iptali</h2>
                <p className="text-xs text-gray-400">
                  Gecikme uyarısı, süre aşımında otomatik iptal ve müşteri mesajı
                </p>
              </div>
            </div>
            <div className="space-y-4">
              {ORDER_LIFECYCLE_NUMERIC_SETTINGS.map((s) => (
                <SettingCard
                  key={s.key}
                  setting={s}
                  currentValue={values[s.key] ?? s.defaultValue}
                  onSave={handleSave}
                />
              ))}
              {ORDER_LIFECYCLE_TEXT_SETTINGS.map((s) => (
                <TextSettingCard
                  key={s.key}
                  setting={s}
                  currentValue={values[s.key] ?? s.defaultValue}
                  onSave={handleSave}
                />
              ))}
            </div>
          </div>

          <p className="text-xs text-gray-400 text-center pt-2 pb-6">
            Kaydedilen değerler birkaç dakika içinde API tarafında kullanılır (önbellek nedeniyle en fazla ~5 dk gecikme olabilir).
          </p>
        </div>
      )}

      {!isLoading && (
        <div className="max-w-2xl mt-8">
          <PlatformLegalDocumentsSection />
        </div>
      )}
    </>
  );
};

export default PlatformSettingsTab;
